(ns ui.gallery.entry-modal
  "The modal of the Replicant gallery that opens when an entry is
   clicked: the preview of the story media, the cards with the things
   the user can do with it (post / schedule, edit, templates) and the
   command bar (delete, archive, duplicate, download). Fed by
   `:query/gallery-entry`. Modal `:params`: the `:story-media/eid`."
  (:require [lambdaisland.uri :as uri]
            [parts.replicant.query :as query]
            [storrito.ui.button :as button]
            [ui.animation-editor.model :as animation-editor]
            [ui.gallery.query :as gq]))

(defn spinner
  []
  [:div.w-4.h-4.rounded-full.border-2.border-gray-300.border-t-secondary-500.animate-spin.inline-block])

(defn preview
  [entry]
  (let [preview-url (:story-media/preview-url entry)
        video-url (:story-media/video-url entry)]
    [:div.relative.flex.justify-center.items-center.shrink-0.bg-gray-200.rounded
     {:class ["w-[180px]" "h-[320px]" "sm:w-[270px]" "sm:h-[480px]"]}
     (cond
       (not preview-url)
       [:div.flex.flex-col.items-center.justify-center.gap-3.text-gray-700.w-full.h-full.bg-gray-300.bg-opacity-50.rounded
        (spinner)
        [:span.text-sm.font-bold
         (or (:story-media/status-message entry)
             "Loading...")]]

       video-url
       [:video.max-h-full.max-w-full
        {:controls true
         :loop true
         :muted true
         :autoplay true
         :playsinline true
         :poster preview-url
         :src video-url
         ;; The `muted` attribute only sets the default of the `muted`
         ;; property while the HTML is parsed. On an element that is
         ;; created by the DOM API it has no effect, so the video would
         ;; play with sound, or not at all where autoplay with sound is
         ;; blocked:
         :replicant/on-mount (fn [{:keys [replicant/node]}]
                               (set! (.-muted node) true))}]

       :else
       [:img.max-h-full.max-w-full.object-contain.block
        {:src preview-url
         :alt (or (:story-media/name entry) "Preview")}])]))

(defn card
  "A card with a primary action button, an info link to a help video and
   a subtitle."
  [{:keys [title subtitle media-src disabled? on-click]}]
  [:div.flex.flex-col.max-w-sm.gap-2.p-2.rounded-lg.bg-white.shadow
   [:div.flex.justify-between.items-center
    [:button
     {:class button/button-primary-css
      :type "button"
      :disabled disabled?
      :on {:click on-click}}
     title]
    (when media-src
      [:a.px-2.text-gray-500.hover:text-gray-800
       {:href media-src
        :target "_blank"
        :title "Watch the video tutorial"}
       [:i.fa.fa-info-circle]])]
   [:div subtitle]])

(def tutorial-url
  "https://youtu.be/1HEJMlbDuN0?t=171")

(defn status-subtitle
  [entry text]
  (if-let [status-message (:story-media/status-message entry)]
    [:b.flex.items-center.gap-2 status-message (spinner)]
    [:div text]))

(defn social-schedule-card
  [entry]
  (card {:title [:span [:i.fas.fa-calendar-plus.mr-2] "Social schedule"]
         :subtitle (status-subtitle entry
                                    "Post or Schedule this media to Instagram, TikTok, and YouTube Shorts from one page")
         :media-src tutorial-url
         :on-click [[:js/location (str "/ui/social-schedule?story-eid="
                                       (:story-media/eid entry))]]}))

(defn editor-url
  [entry]
  (if-let [design-blob-id (:story-media/design-blob-id entry)]
    (str "/ui/editor2?"
         (uri/map->query-string {:story-media-eid (:story-media/eid entry)
                                 :design-blob-id design-blob-id}))
    (str "/ui/editor2?editor1-import="
         (:story-media/eid entry))))

(defn editor-card
  [entry]
  (card {:title "Edit"
         :subtitle [:div "Add " [:b "#hashtags, @usertags, polls and locations"]
                    ". Crop, rotate, and add text"]
         :media-src "https://youtu.be/YDjBEn8cAns?t=52"
         :on-click [[:js/location (editor-url entry)]]}))

(defn template-card
  [entry]
  (card {:title "Templates"
         :subtitle "Create a video by animating text and the background image."
         :media-src "https://www.youtube.com/watch?v=TRFIaxIDw4Q"
         :on-click [[:js/location (animation-editor/new-url (:story-media/eid entry))]]}))

(defn edit-template-card
  [entry]
  (card {:title [:span [:i.fas.fa-pen.mr-2] "Edit"]
         :subtitle "Change text and animations of your story template."
         :media-src "https://www.youtube.com/watch?v=TRFIaxIDw4Q"
         :on-click [[:js/location (animation-editor/edit-url (:story-media/eid entry))]]}))

(defn entry-command
  "A `:data/command` of this slice for the `entry`, which refreshes the
   entry and the gallery afterwards, then runs the `after` actions."
  [kind entry after]
  [:data/command
   {:command/kind kind
    :command/data {:story-media/eid (:story-media/eid entry)}}
   {:on-success (into [[:gallery/entry-query (:story-media/eid entry)]
                       [:gallery/query]]
                      after)
    :on-error [[:ui/show-error]]}])

(defn convert-to-video-card
  [entry]
  (card {:title "Save as Video"
         :subtitle [:span "Convert this Template to a video. Useful to add "
                    [:b "#hashtags, @usertags, locations and polls."]]
         :on-click [(entry-command :command/gallery-animation-to-video
                                  entry
                                  [[:gallery/scroll-top]
                                   [:ui/modal-close]])]}))

(defn card-menu
  [entry]
  [:div.flex.flex-col.justify-center.gap-4
   (social-schedule-card entry)
   (when (:story-media/editor? entry)
     (editor-card entry))
   (when (:story-media/templates? entry)
     (template-card entry))
   (when (:story-media/animation? entry)
     (edit-template-card entry))
   (when (:story-media/animation-to-video? entry)
     (convert-to-video-card entry))])

(defn bar-button
  [{:keys [title label icon on-click]}]
  [:button
   {:class (concat button/css-default button/css-secondary button/css-sm)
    :type "button"
    :title title
    :on {:click on-click}}
   [:i.fa {:class icon}]
   [:div.hidden.sm:block.ml-2 label]])

(defn bulk-command
  "A `:data/command` on the single `entry` that refreshes the gallery
   afterwards, then runs the `after` actions."
  [kind entry after]
  [:data/command
   {:command/kind kind
    :command/data {:story-media/eids #{(:story-media/eid entry)}}}
   {:on-success (into [[:gallery/query]]
                      after)
    :on-error [[:ui/show-error]]}])

(defn command-bar
  [entry]
  [:div.flex.w-full.gap-2.flex-wrap
   (bar-button {:title "Deletes this design"
                :label "Delete"
                :icon "fa-trash"
                :on-click [[:js/confirm "Do you like to delete this story media item?"]
                           (bulk-command :command/gallery-delete
                                         entry
                                         [[:ui/modal-close]])]})
   (if (:story-media/archived-at entry)
     (bar-button {:title "Restore this item from the archive."
                  :label "Restore"
                  :icon "fa-archive"
                  :on-click [(bulk-command :command/gallery-unarchive
                                           entry
                                           [[:gallery/entry-query (:story-media/eid entry)]])]})
     (bar-button {:title "Archives this design"
                  :label "Archive"
                  :icon "fa-archive"
                  :on-click [(bulk-command :command/gallery-archive
                                           entry
                                           [[:gallery/entry-query (:story-media/eid entry)]])]}))
   (bar-button {:title "Duplicates this design"
                :label "Duplicate"
                :icon "fa-copy"
                :on-click [(entry-command :command/gallery-copy
                                          entry
                                          [[:gallery/scroll-top]
                                           [:ui/modal-close]])]})
   (when (:story-media/rendering? entry)
     [:div.flex.items-center.gap-2
      (spinner)
      "rendering"])
   (when (:story-media/render-requestable? entry)
     (bar-button {:title "Creates the video and prepares it for the download"
                  :label "Prepare Download"
                  :icon "fa-download"
                  :on-click [(entry-command :command/gallery-render entry [])]}))
   (when-let [download-url (:story.media/download-url entry)]
     (bar-button {:title "Downloads the story"
                  :label "Download"
                  :icon "fa-download"
                  :on-click [[:url/download
                              download-url
                              (or (:story.media/download-filename entry)
                                  "storrito-story")]]}))
   [:div.flex-grow]
   [:button
    {:class button/button-cancel-css
     :type "button"
     :on {:click [[:ui/modal-close]]}}
    "Cancel"]])

(def content-width-class
  "The width of the loaded modal content on `sm` screens and up: the
   preview plus the cards column."
  ["sm:w-[42rem]"])

(defn render
  [state]
  (let [eid (get-in state [:ui/modal :params :story-media/eid])
        q (gq/entry-query eid)
        entry (query/get-result state q)]
    (cond
      ;; the modal box is as wide as its content (`sm:max-w-fit`, see
      ;; `:gallery/card-click`), so the states before the entry arrived
      ;; take the width of the loaded modal to avoid a jump:
      (nil? entry)
      [:div.flex.items-center.justify-center.py-12.gap-3.text-gray-500
       {:class content-width-class}
       (spinner)
       "Loading..."]

      (:story-media/missing? entry)
      [:div.flex.flex-col.gap-4
       {:class content-width-class}
       [:p "This item no longer exists."]
       [:div.flex.justify-end
        [:button
         {:class button/button-cancel-css
          :type "button"
          :on {:click [[:ui/modal-close] [:gallery/query]]}}
         "Close"]]]

      :else
      [:div.flex.flex-col.gap-4
       (when-let [name (:story-media/name entry)]
         [:h3.text-xl.text-center.w-full.text-ellipsis.overflow-hidden.whitespace-nowrap
          {:title name}
          name])
       [:div.flex.gap-4.flex-col.items-center.sm:flex-row
        (preview entry)
        (card-menu entry)]
       (command-bar entry)])))

(def modal
  {:modal-id :modals/gallery-entry
   :on-load (fn [state]
              [[:gallery/entry-query (get-in state [:ui/modal :params :story-media/eid])]])
   :render #'render})
